'use client'

import { Dropdown16 } from '@vinted/monochrome-icons'
import { Button, Card, Cell, Divider, Icon, Spacer, Text } from '@vinted/web-ui'

import { OutsideClick } from '@marketplace-web/common-components/outside-click-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { LanguageModel } from '@marketplace-web/language/language-data'

import useLanguageSelector from '../useLanguageSelector'

type Props = {
  languages: Array<LanguageModel>
  activeLanguage: LanguageModel
  setActiveLanguage: (language: LanguageModel) => void
}

const LanguageSelectorDropdown = ({ languages, activeLanguage, setActiveLanguage }: Props) => {
  const { isMenuOpen, toggleMenu, hideMenu, saveLanguage } = useLanguageSelector({
    activeLanguage,
    setActiveLanguage,
  })

  const { titleShort } = activeLanguage
  const handleLanguageSelection = (language: LanguageModel) => () => saveLanguage(language)

  const renderLanguageText = (language: LanguageModel) => {
    const { id: activeLanguageId } = activeLanguage

    const activeLanguageProps: Partial<React.ComponentProps<typeof Text>> = {
      theme: 'amplified' as const,
      bold: true,
    }

    const props = language.id === activeLanguageId ? activeLanguageProps : {}

    return (
      <>
        <Text as="span" text={language.titleLocal} {...props} />
        <Spacer orientation="vertical" size="small" />
        <Text as="span" text={`(${language.title})`} {...props} />
      </>
    )
  }

  return (
    <OutsideClick onOutsideClick={hideMenu}>
      <div className="u-ui-padding-left-small">
        <Button
          text={titleShort.toUpperCase()}
          styling="flat"
          size="medium"
          theme="muted"
          inline
          truncated={false}
          icon={<Icon name={Dropdown16} color="greyscale-level-2" />}
          iconPosition="right"
          onClick={toggleMenu}
          aria={{
            'aria-expanded': isMenuOpen,
          }}
          testId="language-selector-button"
        />
        {isMenuOpen && (
          <div className="language-selector__dropdown">
            <Card styling="elevated">
              <div className="u-overflow-hidden">
                <SeparatedList separator={<Divider />}>
                  {languages.map(language => (
                    <Cell
                      type="navigating"
                      onClick={handleLanguageSelection(language)}
                      key={language.id}
                      testId={`language-option-${language.titleShort}`}
                    >
                      {renderLanguageText(language)}
                    </Cell>
                  ))}
                </SeparatedList>
              </div>
            </Card>
          </div>
        )}
      </div>
    </OutsideClick>
  )
}

export default LanguageSelectorDropdown
